// ─── Dementia Case Game — case data ─────────────────────────────────────────

export const DEMENTIA_CASES = [
  {
    id: "alzheimers",
    patient: {
      name: "Mrs. Patricia Davies",
      age: 79,
      sprite: "/blobavatar/blob.doctor.blue.png",
      color: "#8b5cf6",
      history:
        "2-year history of gradually worsening memory. Her daughter reports she forgets appointments, has got lost walking to the local shops (somewhere she has been for 30 years), and asks the same question several times within minutes. Left the hob on twice last week. No hallucinations. No episodes of sudden change. MMSE 18/30. CT brain: generalised cortical atrophy, most prominent in the hippocampal region.",
      flags: [
        "Insidious 2-year decline",
        "Short-term episodic memory mainly affected",
        "Hippocampal atrophy on CT",
        "No hallucinations or Parkinsonism",
      ],
    },
    dementiaTypes: [
      { id: "alzheimers", name: "Alzheimer's disease", correct: true },
      {
        id: "vascular",
        name: "Vascular dementia",
        correct: false,
        feedback:
          "Vascular dementia typically follows a stepwise course linked to strokes or TIAs, with executive dysfunction more prominent than memory loss early on. Mrs. Davies has a smooth gradual decline with hippocampal atrophy — more consistent with Alzheimer's.",
      },
      {
        id: "lewy-body",
        name: "Lewy body dementia",
        correct: false,
        feedback:
          "Lewy body dementia is characterised by fluctuating cognition, well-formed visual hallucinations, Parkinsonism and REM sleep behaviour disorder. None of these features are present here.",
      },
      {
        id: "ftd",
        name: "Frontotemporal dementia",
        correct: false,
        feedback:
          "FTD presents with personality and behaviour change or progressive language problems, often at a younger age (50s–60s). Memory is relatively preserved early — the opposite of Mrs. Davies's presentation.",
      },
    ],
    correctTypeFeedback:
      "Correct! Classic Alzheimer's — insidious onset, gradual progression, episodic memory loss (short-term first), and hippocampal atrophy. Alzheimer's accounts for 60–70% of all dementia cases.",
    managementPoints: [
      {
        id: "m1",
        text: "Refer to a memory clinic for formal assessment, neuropsychological testing and diagnosis confirmation",
        correct: true,
      },
      {
        id: "m2",
        text: "Donepezil (acetylcholinesterase inhibitor) is first-line for mild-to-moderate Alzheimer's — it modestly slows symptom progression",
        correct: true,
      },
      {
        id: "m3",
        text: "Advise she must inform the DVLA and arrange a formal driving assessment — she should not drive until cleared",
        correct: true,
      },
      {
        id: "m4",
        text: "Initiate advance care planning discussions while she still has capacity to express her wishes",
        correct: true,
      },
      {
        id: "m5",
        text: "Haloperidol should be started routinely to prevent behavioural symptoms developing",
        correct: false,
      },
      {
        id: "m6",
        text: "Medication is only useful in severe Alzheimer's — no benefit in treating mild stages",
        correct: false,
      },
    ],
  },
  {
    id: "lewy-body",
    patient: {
      name: "Mr. Robert Singh",
      age: 76,
      sprite: "/blobavatar/blob.moneyman.png",
      color: "#0ea5e9",
      history:
        "18-month history of fluctuating cognition — some days lucid and sharp, other days very confused. His wife reports vivid, well-formed visual hallucinations: he sees children and small animals in the house that are not there, and is not distressed by them. Mild shuffling gait and resting tremor noted on examination. His wife also mentions he has been shouting, kicking and punching in his sleep for the past year. MMSE fluctuates between 15 and 24 on different days.",
      flags: [
        "Fluctuating cognition (day-to-day variation)",
        "Well-formed visual hallucinations",
        "Parkinsonism (shuffling gait, resting tremor)",
        "REM sleep behaviour disorder",
      ],
    },
    dementiaTypes: [
      { id: "lewy-body", name: "Lewy body dementia (DLB)", correct: true },
      {
        id: "alzheimers",
        name: "Alzheimer's disease",
        correct: false,
        feedback:
          "Alzheimer's has a smooth gradual decline with early episodic memory loss. It does not typically cause the fluctuating cognition, well-formed visual hallucinations, or Parkinsonism seen here.",
      },
      {
        id: "vascular",
        name: "Vascular dementia",
        correct: false,
        feedback:
          "Vascular dementia follows a stepwise course after vascular events, with executive dysfunction. It does not produce the classic triad of DLB: fluctuating cognition, visual hallucinations and Parkinsonism.",
      },
      {
        id: "parkinsons-dementia",
        name: "Parkinson's disease dementia",
        correct: false,
        feedback:
          "A close differential — PD dementia has the same features but is only diagnosed when Parkinsonism precedes cognitive decline by more than 1 year (the '1-year rule'). Here both appeared together, pointing to DLB.",
      },
    ],
    correctTypeFeedback:
      "Correct! DLB is defined by the core features: fluctuating cognition, well-formed recurrent visual hallucinations, and Parkinsonism. REM sleep behaviour disorder is a strong supportive feature. The 1-year rule distinguishes it from Parkinson's disease dementia.",
    managementPoints: [
      {
        id: "m1",
        text: "Rivastigmine (acetylcholinesterase inhibitor) is the only licensed treatment for DLB and should be considered",
        correct: true,
      },
      {
        id: "m2",
        text: "AVOID all antipsychotics — severe neuroleptic sensitivity reactions in DLB can be fatal (rigidity, sudden deterioration)",
        correct: true,
      },
      {
        id: "m3",
        text: "Levodopa/carbidopa can be cautiously tried for Parkinsonism but may worsen hallucinations",
        correct: true,
      },
      {
        id: "m4",
        text: "REM sleep behaviour disorder can be managed with clonazepam (low dose) or melatonin",
        correct: true,
      },
      {
        id: "m5",
        text: "Haloperidol is safe and effective for controlling the visual hallucinations in DLB",
        correct: false,
      },
      {
        id: "m6",
        text: "The visual hallucinations should be treated with oral antifungals as they suggest an underlying infection",
        correct: false,
      },
    ],
  },
  {
    id: "vascular",
    patient: {
      name: "Mr. Thomas Williams",
      age: 71,
      sprite: "/blobavatar/blob.beerman.png",
      color: "#dc2626",
      history:
        "Sudden-onset cognitive decline 2 years ago following an ischaemic stroke (left MCA territory). Since then, two further step-wise deteriorations — each after a brief episode of word-finding difficulty. Problems mainly with planning, concentration and word-finding. Memory less severely affected. Emotionally labile with unprovoked crying episodes. Background of hypertension, type 2 diabetes and atrial fibrillation (on warfarin). MRI brain: multiple cortical and subcortical infarcts in a vascular distribution.",
      flags: [
        "Sudden onset following stroke",
        "Stepwise deterioration",
        "Executive dysfunction > memory loss",
        "Multiple infarcts on MRI",
        "Vascular risk factors: hypertension, T2DM, AF",
      ],
    },
    dementiaTypes: [
      { id: "vascular", name: "Vascular dementia", correct: true },
      {
        id: "alzheimers",
        name: "Alzheimer's disease",
        correct: false,
        feedback:
          "Alzheimer's has an insidious gradual onset with early episodic memory loss — not sudden-onset stepwise decline following a stroke. The vascular history and MRI findings here are key.",
      },
      {
        id: "lewy-body",
        name: "Lewy body dementia",
        correct: false,
        feedback:
          "DLB features fluctuating cognition, visual hallucinations and Parkinsonism — none of which are present here. The post-stroke stepwise course with MRI infarcts points to vascular dementia.",
      },
      {
        id: "ftd",
        name: "Frontotemporal dementia",
        correct: false,
        feedback:
          "FTD presents with personality change or language problems at a younger age, without a vascular history or infarcts on imaging. Mr. Williams's presentation is clearly vascular in origin.",
      },
    ],
    correctTypeFeedback:
      "Correct! Vascular dementia — sudden onset after a stroke, stepwise progression with further events, executive dysfunction more than memory loss, emotional lability, and multiple infarcts on MRI. Optimising vascular risk factors is key to preventing further decline.",
    managementPoints: [
      {
        id: "m1",
        text: "Optimise vascular risk factors: tight blood pressure control, HbA1c management, and ensure INR is therapeutic (warfarin for AF)",
        correct: true,
      },
      {
        id: "m2",
        text: "There are no specifically licensed drugs for vascular dementia — unlike Alzheimer's, AChEIs are not routinely recommended",
        correct: true,
      },
      {
        id: "m3",
        text: "Emotional lability may respond to an SSRI (e.g. sertraline)",
        correct: true,
      },
      {
        id: "m4",
        text: "Falls risk assessment is essential — cognitive impairment plus anticoagulation significantly increases the risk of serious bleeding from falls",
        correct: true,
      },
      {
        id: "m5",
        text: "Donepezil is the standard first-line pharmacological treatment for vascular dementia",
        correct: false,
      },
      {
        id: "m6",
        text: "Stopping antihypertensives is recommended to prevent postural hypotension and further falls",
        correct: false,
      },
    ],
  },
  {
    id: "ftd",
    patient: {
      name: "Mrs. Helen Carter",
      age: 62,
      sprite: "/blobavatar/blob.doctor.blue.png",
      color: "#f97316",
      history:
        "3-year history of marked personality change in a previously reserved and professional woman. She now makes sexually inappropriate remarks to care staff, has twice been caught shoplifting items she did not need, and has shown poor financial judgement (gave away large sums of money). Neglects personal hygiene. Shows little insight or concern about her behaviour. Memory for day-to-day events is relatively preserved. Emerging word-finding difficulties noticed over the past 6 months. CT brain: prominent frontal and temporal lobe atrophy bilaterally.",
      flags: [
        "Younger onset (age 62)",
        "Personality change and disinhibition",
        "Relatively preserved memory",
        "Frontal and temporal atrophy on CT",
        "Poor insight",
      ],
    },
    dementiaTypes: [
      { id: "ftd", name: "Frontotemporal dementia (FTD)", correct: true },
      {
        id: "alzheimers",
        name: "Alzheimer's disease",
        correct: false,
        feedback:
          "Alzheimer's typically begins with episodic memory loss and hippocampal atrophy, in older patients. Mrs. Carter's memory is relatively preserved, and her frontal presentation with personality change, disinhibition and younger onset strongly favours FTD.",
      },
      {
        id: "vascular",
        name: "Vascular dementia",
        correct: false,
        feedback:
          "Vascular dementia is linked to vascular events and risk factors, with a stepwise course. There is no such history here, and the atrophy is frontal/temporal rather than in a vascular distribution.",
      },
      {
        id: "lewy-body",
        name: "Lewy body dementia",
        correct: false,
        feedback:
          "DLB features fluctuating cognition, visual hallucinations and Parkinsonism — none are present here. The prominent behavioural change with frontal atrophy in a younger patient is classic FTD.",
      },
    ],
    correctTypeFeedback:
      "Correct! Frontotemporal dementia — characterised by behavioural variant (disinhibition, apathy, loss of empathy, compulsive behaviours) or progressive language syndromes. Onset is typically in the 50s–60s, and memory is often spared early. FTD accounts for up to 20% of early-onset dementia.",
    managementPoints: [
      {
        id: "m1",
        text: "There is no approved pharmacological treatment for FTD — management is primarily behavioural and supportive",
        correct: true,
      },
      {
        id: "m2",
        text: "SSRIs (e.g. sertraline) may help reduce disinhibition and compulsive behaviours",
        correct: true,
      },
      {
        id: "m3",
        text: "Referral to specialist behavioural neurology or neuropsychiatry is essential for diagnosis and management planning",
        correct: true,
      },
      {
        id: "m4",
        text: "Carer and family support is critical — behavioural symptoms cause extreme distress and carers have a high burden",
        correct: true,
      },
      {
        id: "m5",
        text: "Donepezil is first-line pharmacological treatment for frontotemporal dementia",
        correct: false,
      },
      {
        id: "m6",
        text: "The personality changes are a side effect of her medications and will resolve once they are stopped",
        correct: false,
      },
    ],
  },
];
